import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  ARTICLE_BOUGHT_WITHIN,
  fetchArticleFilterOptions,
  listArticles,
  type ArticleActivityPeriod,
  type ArticleBoughtWithin,
  type ArticleInsight,
} from "../api/articles";
import AddToCartButton from "../components/AddToCartButton";
import { TableHeadCell, TableLoadMoreFooter, TablePanel } from "../components/DataTable";
import EmptyState, { emptyStateActionClass } from "../components/EmptyState";
import { FilterChoiceRow, FilterTagRow, type FilterTagOption } from "../components/FilterTags";
import PersonalViewNotice, { PersonalViewReset } from "../components/PersonalViewNotice";
import SearchInput from "../components/SearchInput";
import Spinner from "../components/Spinner";
import { activityPeriodHint, formatInvoiceDocumentCount12m } from "../lib/articleActivity";
import { cleanDescription } from "../lib/description";
import { formatDateShort, formatEuro } from "../lib/format";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { usePersonalView } from "../lib/usePersonalView";
import { useViewSearch } from "../lib/useViewSearch";

const PAGE_SIZE = 50;
type ListOrder = "last_order" | "frequency";
const DEFAULT_LABEL = "Alle Artikel, zuletzt gekauft zuerst";

/**
 * Artikel finden und wählen (Beta-Feedback 2026-10-08, Entwurf A3): große
 * Suche, feste Filter als Tags (Hersteller, Großhändler, Kostenstelle,
 * Zeitraum) und eine ruhige Liste mit letztem Preis und Warenkorb. Ausgaben
 * und Mehrkosten stehen in der Analyse, nicht hier.
 */
export default function ArticlesOverviewPage() {
  const personalView = usePersonalView("articles");
  const params = personalView.params;
  const order: ListOrder = params.get("sort") === "frequency" ? "frequency" : "last_order";
  const { search, setSearch, searchPending } = useViewSearch(personalView);
  const searchText = params.get("q")?.trim() ?? "";
  const manufacturers = unique(params.getAll("manufacturer"));
  const suppliers = unique(params.getAll("supplier"));
  const costCenters = unique(params.getAll("cost_center"));
  const bought = boughtWithin(params.get("bought"));

  function change(mutate: (next: URLSearchParams) => void) {
    const next = new URLSearchParams(params);
    mutate(next);
    personalView.change(next);
  }
  function toggle(key: string, value: string) {
    change((next) => {
      const values = next.getAll(key);
      next.delete(key);
      (values.includes(value) ? values.filter((v) => v !== value) : [...values, value]).forEach((v) => next.append(key, v));
    });
  }
  const clear = (key: string) => change((next) => next.delete(key));
  const anyFilter = Boolean(manufacturers.length || suppliers.length || costCenters.length || bought);
  function resetAll() {
    change((next) => {
      for (const key of ["q", "manufacturer", "supplier", "cost_center", "bought"]) next.delete(key);
    });
  }

  const listQuery = useInfiniteQuery({
    queryKey: ["articles", "overview", searchText ? "relevance" : order, searchText, manufacturers, suppliers, costCenters, bought],
    queryFn: ({ pageParam }) =>
      listArticles({
        limit: PAGE_SIZE,
        offset: pageParam,
        withTotal: true,
        sort: searchText ? "relevance" : order,
        q: searchText || undefined,
        manufacturerIds: manufacturers,
        suppliers,
        costCenters,
        boughtWithinMonths: bought ?? undefined,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const nextOffset = last.offset + last.items.length;
      if (last.items.length < last.limit || (last.total != null && nextOffset >= last.total)) return undefined;
      return nextOffset;
    },
    enabled: personalView.ready,
    staleTime: 60_000,
  });

  // Choices follow the search and the period; selected cost centres keep their name.
  const optionsQuery = useQuery({
    queryKey: ["articles", "filter-options", searchText, bought, costCenters],
    queryFn: () => fetchArticleFilterOptions({ q: searchText || undefined, boughtWithinMonths: bought ?? undefined, selectedCostCenters: costCenters }),
    enabled: personalView.ready,
    staleTime: 300_000,
    placeholderData: keepPreviousData,
  });
  const [costCenterSearch, setCostCenterSearch] = useState("");
  const costCenterQ = useDebouncedValue(costCenterSearch, 250).trim();
  const costCenterSearchQuery = useQuery({
    queryKey: ["articles", "filter-options", "cost-center-search", searchText, bought, costCenterQ],
    queryFn: () => fetchArticleFilterOptions({ q: searchText || undefined, boughtWithinMonths: bought ?? undefined, costCenterQ }),
    enabled: personalView.ready && costCenterQ.length > 0,
    staleTime: 300_000,
  });

  const options = optionsQuery.data;
  const manufacturerOptions = useMemo<FilterTagOption[]>(
    () => (options?.manufacturers ?? []).map((row) => ({ value: row.id, label: row.name ?? row.id, count: row.article_count })),
    [options],
  );
  const supplierOptions = useMemo<FilterTagOption[]>(
    () => (options?.suppliers ?? []).map((row) => ({ value: row.supplier, label: row.supplier, count: row.article_count })),
    [options],
  );
  const toCostCenterOption = (row: { key: string; label: string | null; article_count: number }): FilterTagOption => ({
    value: row.key,
    // Some sources store the raw key as label ("address:…"); show it without the prefix.
    label: (row.label ?? row.key).replace(/^(address|ref|code|project):/, ""),
    count: row.article_count,
  });
  const costCenterOptions = useMemo(() => (options?.cost_centers ?? []).map(toCostCenterOption), [options]);
  const costCenterPickerOptions = costCenterQ
    ? (costCenterSearchQuery.data?.cost_centers ?? []).map(toCostCenterOption)
    : costCenterOptions;

  const refreshing = !personalView.ready || searchPending || listQuery.isLoading || (listQuery.isFetching && !listQuery.isFetchingNextPage);
  const items = refreshing ? [] : listQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const total = refreshing ? null : listQuery.data?.pages[0]?.total ?? null;
  const tokens = searchTokens(searchText);
  const lastPurchase = options?.last_purchase_date ?? null;
  const activityPeriod = listQuery.data?.pages[0]?.activity_period;

  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Artikel</h1>
        <PersonalViewReset view={personalView} defaultLabel={DEFAULT_LABEL} />
      </header>
      <PersonalViewNotice view={personalView} defaultLabel={DEFAULT_LABEL} />

      <div className="mt-4">
        <SearchInput
          size="large"
          disabled={!personalView.ready}
          value={search}
          onChange={setSearch}
          placeholder="Artikel suchen – Nummer, Bezeichnung oder Maß"
          ariaLabel="Artikelliste durchsuchen"
        />
      </div>

      <section aria-label="Artikel filtern" className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm">
        <FilterTagRow
          label="Hersteller"
          options={manufacturerOptions}
          selected={manufacturers}
          onToggle={(value) => toggle("manufacturer", value)}
          onClear={() => clear("manufacturer")}
          top={6}
          allLabel={manufacturerOptions.length ? `Alle ${manufacturerOptions.length.toLocaleString("de-AT")} Hersteller` : undefined}
        />
        <FilterTagRow
          label="Großhändler"
          options={supplierOptions}
          selected={suppliers}
          onToggle={(value) => toggle("supplier", value)}
          onClear={() => clear("supplier")}
          top={8}
          allLabel={supplierOptions.length > 8 ? `Alle ${supplierOptions.length} Großhändler` : undefined}
        />
        <FilterTagRow
          label="Kostenstelle"
          options={costCenterOptions}
          selected={costCenters}
          onToggle={(value) => toggle("cost_center", value)}
          onClear={() => clear("cost_center")}
          top={5}
          allLabel={options?.cost_center_total ? `Alle ${options.cost_center_total.toLocaleString("de-AT")} Kostenstellen` : undefined}
          pickerOptions={costCenterPickerOptions}
          onPickerSearch={setCostCenterSearch}
          pickerLoading={costCenterSearchQuery.isFetching}
        />
        <FilterChoiceRow<ArticleBoughtWithin>
          label="Gekauft"
          options={ARTICLE_BOUGHT_WITHIN.map((months) => ({ value: months, label: `Letzte ${months} Monate` }))}
          value={bought}
          onChange={(next) => change((params) => (next ? params.set("bought", String(next)) : params.delete("bought")))}
        />
      </section>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label="Reihenfolge" className={`inline-flex rounded-md border border-slate-300 bg-white p-0.5 text-sm shadow-sm ${searchText ? "invisible" : ""}`}>
          {([["last_order", "Zuletzt gekauft"], ["frequency", "Häufig gekauft"]] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={order === value}
              onClick={() => change((next) => (value === "last_order" ? next.delete("sort") : next.set("sort", value)))}
              className={`rounded px-3 py-1 font-medium ${order === value ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <button type="button" onClick={() => downloadCsv(items, activityPeriod, bought)} disabled={items.length === 0} className="h-9 shrink-0 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50">
            Export für Excel
          </button>
        </div>
      </div>

      <TablePanel
        className="mt-2"
        footer={
          listQuery.isError || refreshing || items.length === 0 ? null : (
            <TableLoadMoreFooter
              loadedCount={items.length}
              totalCount={total}
              itemLabel="Artikel"
              hasMore={Boolean(listQuery.hasNextPage)}
              isLoading={listQuery.isFetchingNextPage}
              buttonLabel="Weitere Artikel laden"
              loadingLabel="Lade weitere Artikel …"
              onLoadMore={() => void listQuery.fetchNextPage()}
            />
          )
        }
      >
        {refreshing ? (
          <div className="flex h-40 items-center justify-center"><Spinner size="sm" label="Lade Artikel …" /></div>
        ) : listQuery.isError ? (
          <p role="alert" className="px-4 py-6 text-sm text-rose-700">Die Artikel konnten nicht geladen werden: {(listQuery.error as Error).message}</p>
        ) : items.length === 0 ? (
          <div className="px-4 py-3">
            <EmptyState
              title={searchText || anyFilter ? "Keine passenden Artikel gefunden" : "Noch keine Artikel vorhanden"}
              actions={searchText || anyFilter ? (
                <button type="button" className={emptyStateActionClass} onClick={resetAll}>Suche und Filter zurücksetzen</button>
              ) : (
                <Link to="/imports" className={emptyStateActionClass}>Importe öffnen</Link>
              )}
            >
              {bought && lastPurchase && lastPurchase < monthsAgo(bought)
                ? `In den letzten ${bought} Monaten wurde nichts gekauft. Belege liegen bis ${formatDateShort(lastPurchase)} vor.`
                : searchText || anyFilter
                  ? "Versuche eine kürzere Nummer oder Bezeichnung oder entferne einen Filter."
                  : "Hier erscheinen Artikel aus den eingelesenen Unterlagen."}
            </EmptyState>
          </div>
        ) : (
          <>
            {/* Desktop: Standardtabelle wie die Belegpositionen. */}
            <table aria-label="Artikelliste" className="hidden w-full table-fixed text-sm md:table">
              <colgroup>
                <col className="w-[35%]" />
                <col className="w-[13%]" />
                <col className="w-[9%]" />
                <col className="w-[13%]" />
                <col className="w-[10%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <TableHeadCell>Artikel</TableHeadCell>
                  <TableHeadCell>Hersteller</TableHeadCell>
                  <TableHeadCell align="right" hint={bought ? `Rechnungen der letzten ${bought} Monate; keine Gutschriften.` : activityPeriodHint(activityPeriod, "rolling12m")}>Käufe {bought ?? 12} M</TableHeadCell>
                  <TableHeadCell>Zuletzt gekauft</TableHeadCell>
                  <TableHeadCell align="right" hint="Letzter Netto-Einheitspreis">Letzter Preis</TableHeadCell>
                  <TableHeadCell align="right">Warenkorb</TableHeadCell>
                </tr>
              </thead>
              <tbody>
                {items.map((article) => <ArticleTableRow key={article.article_group_key} article={article} tokens={tokens} />)}
              </tbody>
            </table>
            {/* Handy: gestapelte Karten. */}
            <ul aria-label="Artikelliste" className="md:hidden">
              {items.map((article) => <ArticleCard key={article.article_group_key} article={article} tokens={tokens} months={bought ?? 12} />)}
            </ul>
          </>
        )}
      </TablePanel>
    </main>
  );
}

function useArticleView(article: ArticleInsight) {
  const location = useLocation();
  return {
    href: `/articles/${encodeURIComponent(article.article_group_key)}`,
    from: location.pathname + location.search,
    code: article.primary_article_code ?? article.article_group_key,
    description: article.description_example ? cleanDescription(article.description_example) : article.product_family ?? null,
    manufacturer: article.brand_name ?? article.manufacturer_name ?? article.manufacturer_id ?? null,
    last: article.last_invoice_doc_date ?? article.last_order_doc_date,
  };
}

/** Artikelnummer grün in Monospace, darunter die Bezeichnung – wie in den Belegpositionen. */
function ArticleName({ article, tokens }: { article: ArticleInsight; tokens: readonly string[] }) {
  const view = useArticleView(article);
  return (
    <div className="min-w-0">
      <Link
        to={view.href}
        state={{ from: view.from }}
        className="inline-block max-w-full break-all align-bottom font-mono text-xs font-medium text-emerald-700 hover:underline md:truncate"
      >
        {highlight(view.code, tokens)}
      </Link>
      {view.description ? (
        <span className="mt-0.5 block break-words text-slate-600 md:truncate" title={view.description}>
          {highlight(view.description, tokens)}
        </span>
      ) : null}
    </div>
  );
}

function ArticleTableRow({ article, tokens }: { article: ArticleInsight; tokens: readonly string[] }) {
  const view = useArticleView(article);
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-article-row>
      <td className="px-3 py-2.5"><ArticleName article={article} tokens={tokens} /></td>
      <td className="truncate px-3 py-2.5 text-slate-600">{view.manufacturer ?? "–"}</td>
      <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">{formatInvoiceDocumentCount12m(purchases(article))}</td>
      <td className="px-3 py-2.5 tabular-nums text-slate-600">{view.last ? formatDateShort(view.last) : "–"}</td>
      <td className="px-3 py-2.5 text-right font-medium tabular-nums text-slate-900">{formatEuro(article.last_net_unit_price)}</td>
      <td className="whitespace-nowrap px-3 py-2.5 text-right">
        <AddToCartButton articleGroupKey={article.article_group_key} articleLabel={article.primary_article_code ?? "Artikel"} compact />
      </td>
    </tr>
  );
}

function ArticleCard({ article, tokens, months }: { article: ArticleInsight; tokens: readonly string[]; months: number }) {
  const view = useArticleView(article);
  return (
    <li className="border-t border-slate-100 px-4 py-3 first:border-t-0" data-article-row>
      <div className="flex items-start justify-between gap-3">
        <ArticleName article={article} tokens={tokens} />
        <span className="shrink-0 text-sm font-medium tabular-nums text-slate-900">{formatEuro(article.last_net_unit_price)}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {[
          view.manufacturer,
          purchases(article) ? `${formatInvoiceDocumentCount12m(purchases(article))}× in ${months} M` : null,
          view.last ? `zuletzt ${formatDateShort(view.last)}` : null,
        ].filter(Boolean).join(" · ")}
      </p>
      <div className="mt-2">
        <AddToCartButton articleGroupKey={article.article_group_key} articleLabel={article.primary_article_code ?? "Artikel"} compact />
      </div>
    </li>
  );
}

/** Käufe im gewählten Zeitraum; ohne Zeitraum die letzten 12 Monate. */
function purchases(article: ArticleInsight): number {
  return article.invoice_document_count_window ?? article.invoice_document_count_12m;
}

function unique(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function boughtWithin(value: string | null): ArticleBoughtWithin | null {
  const months = Number(value);
  return (ARTICLE_BOUGHT_WITHIN as readonly number[]).includes(months) ? (months as ArticleBoughtWithin) : null;
}

function monthsAgo(months: number, today = new Date()): string {
  const date = new Date(today.getFullYear(), today.getMonth() - months, today.getDate());
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Same words as the server search: lower case, inch marks dropped. */
function searchTokens(q: string): string[] {
  return q.toLowerCase().replace(/["″]/g, " ").split(/\s+/).filter(Boolean);
}

function highlight(text: string, tokens: readonly string[]) {
  if (!tokens.length) return text;
  const pattern = new RegExp(`(${tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return text.split(pattern).map((part, index) =>
    index % 2 === 1 ? <mark key={index} className="rounded-sm bg-amber-100 text-inherit">{part}</mark> : part,
  );
}

function downloadCsv(items: readonly ArticleInsight[], period: ArticleActivityPeriod | undefined, months: number | null) {
  // Same activity metadata as before, so an export says which 12 months it counts.
  const meta = [
    period?.as_of_date ?? null,
    period?.rolling_12m_from ?? null,
    period?.ytd_from ?? null,
    period?.time_zone ?? null,
  ];
  const columns = [
    "article_group_key",
    "primary_article_code",
    "manufacturer_id",
    "brand_name",
    "description_example",
    "invoice_document_count_12m",
    "last_invoice_doc_date",
    "last_invoice_doc_id",
    "last_net_unit_price",
  ] as const;
  const cell = (value: unknown) => {
    if (value == null) return "";
    const text = String(value).replace(/"/g, '""');
    return /[;"\n]/.test(text) ? `"${text}"` : text;
  };
  const header = [
    "activity_period_as_of_date",
    "activity_period_rolling_12m_from",
    "activity_period_ytd_from",
    "activity_period_time_zone",
    ...columns,
    "bought_within_months",
    "invoice_document_count_window",
  ];
  const rows = items.map((item) =>
    [...meta, ...columns.map((column) => item[column]), months, purchases(item)].map(cell).join(";"),
  );
  const blob = new Blob(["﻿" + [header.join(";"), ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "artikel.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
